import React, { useMemo, useState } from 'react'
import {
  ChartingTabs,
  CHARTING_TABS_DEMO_ITEMS,
  CHARTING_TABS_LIVE_ITEMS,
  CHARTING_TABS_PREVIEW_STATE_LABELS,
  CHARTING_TABS_PREVIEW_STATES,
  type ChartingTabsPreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ChartingTabsShowcase.css'

const ChartingTabsShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState(CHARTING_TABS_LIVE_ITEMS[0].id)
  const [highlightedState, setHighlightedState] =
    useState<ChartingTabsPreviewState>('default')
  const [darkMode, setDarkMode] = useState(false)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'charting-tabs-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="charting-tabs-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as ChartingTabsPreviewState,
                  )
                }
              >
                {CHARTING_TABS_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {CHARTING_TABS_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'charting-tabs-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="charting-tabs-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedState],
  )

  const activeLabel =
    CHARTING_TABS_LIVE_ITEMS.find((item) => item.id === activeTab)?.label ??
    'Tab'

  return (
    <>
      <UiPreviewCanvas
        title="Charting tabs"
        description="96px segments on a filled track; selected is raised, hover tints the idle tab."
        stageClassName="ui-preview__stage--wide charting-tabs-showcase__stage-wrap"
      >
        <div
          className={`charting-tabs-showcase__sheet${
            darkMode ? ' charting-tabs-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click a tab to switch panels.
            </p>
            <div
              className={`charting-tabs-showcase__panel${
                darkMode ? ' charting-tabs-showcase__panel--dark' : ''
              }`}
            >
              <ChartingTabs
                items={CHARTING_TABS_LIVE_ITEMS}
                value={activeTab}
                darkMode={darkMode}
                onChange={setActiveTab}
              />
              <div
                className={`charting-tabs-showcase__content${
                  darkMode ? ' charting-tabs-showcase__content--dark' : ''
                }`}
              >
                <h4 className="charting-tabs-showcase__content-title">
                  {activeLabel}
                </h4>
                <p className="charting-tabs-showcase__content-body">
                  Content for the selected charting tab appears here.
                </p>
              </div>
            </div>
          </section>

          <section className="charting-tabs-showcase__matrix">
            <h4 className="charting-tabs-showcase__section-title">States</h4>
            {CHARTING_TABS_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`charting-tabs-showcase__row${
                  previewState === highlightedState
                    ? ' charting-tabs-showcase__row--selected'
                    : ''
                }${darkMode ? ' charting-tabs-showcase__row--dark' : ''}`}
              >
                <span className="charting-tabs-showcase__row-label">
                  {CHARTING_TABS_PREVIEW_STATE_LABELS[previewState]}
                </span>
                <ChartingTabs
                  items={CHARTING_TABS_DEMO_ITEMS}
                  previewState={previewState}
                  darkMode={darkMode}
                />
              </div>
            ))}
          </section>

          <section className="charting-tabs-showcase__matrix">
            <h4 className="charting-tabs-showcase__section-title">
              Light and dark
            </h4>
            <p className="ui-showcase-live__note">
              Light (`blue-300` track) and dark (`blue-800` track).
            </p>
            {CHARTING_TABS_PREVIEW_STATES.map((previewState) => (
              <div
                key={`light-${previewState}`}
                className="charting-tabs-showcase__row"
              >
                <span className="charting-tabs-showcase__row-label">
                  Light · {CHARTING_TABS_PREVIEW_STATE_LABELS[previewState]}
                </span>
                <ChartingTabs
                  items={CHARTING_TABS_DEMO_ITEMS}
                  previewState={previewState}
                />
              </div>
            ))}
            {CHARTING_TABS_PREVIEW_STATES.map((previewState) => (
              <div
                key={`dark-${previewState}`}
                className="charting-tabs-showcase__row charting-tabs-showcase__row--dark"
              >
                <span className="charting-tabs-showcase__row-label">
                  Dark · {CHARTING_TABS_PREVIEW_STATE_LABELS[previewState]}
                </span>
                <ChartingTabs
                  items={CHARTING_TABS_DEMO_ITEMS}
                  previewState={previewState}
                  darkMode
                />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Charting tabs"
        sections={propertySections}
      />
    </>
  )
}

export default ChartingTabsShowcase
